import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Check, Download, Save, Sparkles } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  usePopularDecksQuery,
  useSaveDeckMutation,
  useCoachDeckListMutation,
  useMeQuery,
} from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { CoachAnalysis } from '@/components/cr/CoachAnalysis';
import { coachAnalysisError } from '@/components/cr/coachError';
import { Paywall } from '@/components/cr/Paywall';
import { cn } from '@/lib/cn';
import { crDeckImportUrl } from '@/lib/crDeckLink';
import type { PopularDeck } from '@/types/qbot';

const ARCHETYPES = ['', 'Beatdown', 'Control', 'Cycle', 'Siege', 'Bait', 'Bridge Spam'];

/**
 * Meta deck browser — Top 200 ladder aggregations from QBot's `DeckSnapshot`
 * table. Archetype filter is a horizontal chip rail; cards filter is
 * intentionally left out of the MVP (requires the card-name picker which
 * is a bigger UI chunk; ship after the picker lands).
 *
 * "Save" copies the deck into the user's saved list, archetype detected
 * server-side.
 */
export function CRPopularDecks() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [archetype, setArchetype] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isFetching, error } = usePopularDecksQuery({
    archetype: archetype || undefined,
    page,
    pageSize,
  });
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();
  const { data: identity } = useMeQuery();
  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 py-4">
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-fg">{t('popular.title')}</h1>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ARCHETYPES.map((a) => (
            <button
              key={a || 'all'}
              type="button"
              onClick={() => {
                haptics.selection();
                setArchetype(a);
                setPage(1);
              }}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors',
                archetype === a
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              {a || t('popular.filter.all')}
            </button>
          ))}
        </div>
      </header>

      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {t('popular.error')}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {data?.decks.map((deck, idx) => (
          <PopularDeckCard
            key={`${deck.cardIds.join(',')}-${idx}`}
            deck={deck}
            isPro={isPro}
            saving={saving}
            onSave={async (name) => {
              try {
                await saveDeck({ name, cards: deck.cards.map((c) => c.name) }).unwrap();
                haptics.notification('success');
                return true;
              } catch {
                haptics.notification('error');
                return false;
              }
            }}
          />
        ))}
      </ul>

      {data && (
        <nav className="mt-auto flex items-center justify-between rounded-lg bg-bg-secondary p-2">
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-xs text-fg-muted tabular-nums">
            {t('popular.page', { page, total: data.pagination.totalPages || 1 })}
          </span>
          <button
            type="button"
            disabled={page >= (data.pagination.totalPages || 1) || isFetching}
            onClick={() => setPage((p) => p + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      )}
    </div>
  );
}

interface PopularDeckCardProps {
  deck: PopularDeck;
  isPro: boolean;
  saving: boolean;
  onSave: (name: string) => Promise<boolean>;
}

const MAX_PLAYER_NAMES = 8;

function PopularDeckCard({ deck, isPro, saving, onSave }: PopularDeckCardProps) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [justSaved, setJustSaved] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analyse, { data: analysisData, isLoading: analysing, error: analysisError }] =
    useCoachDeckListMutation();

  const onAnalyse = () => {
    haptics.impact('medium');
    setAnalysisOpen(true);
    analyse({ cards: deck.cards.map((c) => c.name) }).catch(() => undefined);
  };

  // Names of the (sampled) players running this deck — surfaced under the
  // usage count. Capped so a popular deck doesn't render a wall of names.
  const playerNames = (deck.players ?? []).map((p) => p.name).filter(Boolean);
  const shownNames = playerNames.slice(0, MAX_PLAYER_NAMES);
  const extraNames = playerNames.length - shownNames.length;

  const handleSave = async () => {
    if (saving || justSaved) return;
    const ok = await onSave(`${deck.archetype} top deck`);
    if (ok) setJustSaved(true);
  };

  const handleImport = () => {
    haptics.impact('light');
    openLink.ifAvailable(crDeckImportUrl(deck.cards));
  };

  return (
    <li className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-fg">{deck.archetype}</p>
          <p className="text-[11px] text-fg-muted">
            {t('popular.usage', { count: deck.count })}
            {deck.winRate ? ` · ${Math.round(deck.winRate * 100)}% WR` : ''}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleImport}
            className="flex items-center gap-1 rounded-md bg-bg-tertiary px-2.5 py-1 text-[11px] font-semibold text-brand transition-opacity active:opacity-80"
          >
            <Download className="h-3.5 w-3.5" />
            {t('popular.import', { defaultValue: 'Import' })}
          </button>
          <button
            type="button"
            disabled={saving || justSaved}
            onClick={handleSave}
            className={cn(
              'flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-opacity active:opacity-80 disabled:opacity-50',
              justSaved ? 'bg-success/15 text-success' : 'bg-bg-tertiary text-brand',
            )}
          >
            {justSaved ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
            {justSaved ? t('popular.saved', { defaultValue: 'Saved' }) : t('popular.save')}
          </button>
        </div>
      </div>
      {shownNames.length > 0 && (
        <p className="mb-2 text-[11px] leading-snug text-fg-muted">
          <span className="font-semibold">{t('popular.players')}:</span>{' '}
          {shownNames.join(', ')}
          {extraNames > 0 ? ` ${t('popular.morePlayers', { count: extraNames })}` : ''}
        </p>
      )}
      {justSaved && (
        <p className="mb-2 text-[11px] text-success">
          {t('popular.saved_confirm', { defaultValue: 'Deck saved to your list.' })}
        </p>
      )}
      <DeckGrid cards={deck.cards} avgElixir={deck.avgElixir} showLevels={false} />

      {isPro ? (
        !analysisOpen ? (
          <button
            type="button"
            onClick={onAnalyse}
            className="mt-2 flex items-center gap-1.5 rounded-md bg-bg-tertiary px-2.5 py-1 text-[11px] font-semibold text-brand transition-opacity active:opacity-80"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t('analysis.cta')}
          </button>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {analysing && (
              <div className="flex items-center gap-2 rounded-md bg-bg-tertiary p-3 text-xs text-fg-muted">
                <Sparkles className="h-4 w-4 animate-pulse text-brand" />
                {t('analysis.thinking')}
              </div>
            )}
            {!!analysisError && (
              <p className="rounded-md bg-rose-500/10 p-3 text-xs text-rose-300">
                {coachAnalysisError(analysisError, t)}
              </p>
            )}
            {analysisData?.analysis && (
              <CoachAnalysis analysis={analysisData.analysis} usage={analysisData.usage} />
            )}
          </div>
        )
      ) : (
        <details className="mt-2">
          <summary className="flex cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-brand">
            <Sparkles className="h-3.5 w-3.5" />
            {t('analysis.cta')}
          </summary>
          <div className="mt-2">
            <Paywall feature={t('coach.title')} />
          </div>
        </details>
      )}
    </li>
  );
}
